/**
 * Approvals — admin queue for weekly timesheet submissions.
 *
 * Each pending submission shows the employee, week range, total hours, and
 * inline approve/reject controls (with a note). Approve locks the week
 * permanently; reject unlocks edits for the employee so they can amend
 * and resubmit.
 *
 * Admin-only; sidebar hides this for employees and backend re-enforces
 * via `timesheet.approve`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type CSSProperties } from "react";

type CellPatch = { category: string; work_date: string; hours: number };

import { Avatar } from "@m2s/design-system";

import { TimesheetGridView } from "@/components/TimesheetGridView";
import { api, downloadBlob, extractErrorMessage } from "@/lib/api";
import type { TimesheetGrid, TimesheetSubmission } from "@/lib/types";
import { useAuthStore } from "@/store/auth";

const ADMIN_ROLES = new Set(["admin"]);

function hms(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}
function fmtWeekRange(weekStartISO: string): string {
  const start = new Date(weekStartISO + "T00:00:00");
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const sameMonth = start.getMonth() === end.getMonth();
  return sameMonth
    ? `${start.toLocaleDateString(undefined, opts)} – ${end.getDate()}, ${end.getFullYear()}`
    : `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}, ${end.getFullYear()}`;
}
function initialsOf(name: string | null): string {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function ApprovalsPage() {
  const memberships = useAuthStore((s) => s.memberships);
  const isAdmin = memberships.some((m) => ADMIN_ROLES.has(m.role));
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<
    "submitted" | "download_requested" | "approved" | "rejected" | ""
  >("submitted");
  const [noteFor, setNoteFor] = useState<Record<string, string>>({});
  const [managerFor, setManagerFor] = useState<Record<string, string>>({});
  const [rateFor, setRateFor] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const queueQuery = useQuery({
    queryKey: ["admin-timesheet-submissions", statusFilter],
    queryFn: async () =>
      (
        await api.get<TimesheetSubmission[]>(
          `/admin/timesheet-submissions${statusFilter ? `?status=${statusFilter}` : "?status="}`,
        )
      ).data,
    enabled: isAdmin,
  });

  const decide = useMutation({
    mutationFn: async (vars: {
      id: string;
      decision: "approve" | "reject";
      note: string;
      manager: string;
      rate: string;
    }) =>
      (
        await api.post<TimesheetSubmission>(
          `/admin/timesheet-submissions/${vars.id}/decision`,
          {
            decision: vars.decision,
            note: vars.note || null,
            // Send only on approve; backend ignores either way for reject.
            manager_name:
              vars.decision === "approve" ? vars.manager.trim() || null : null,
            hourly_rate_usd:
              vars.decision === "approve" && vars.rate.trim() !== ""
                ? Number(vars.rate)
                : null,
          },
        )
      ).data,
    onSuccess: () => {
      setError(null);
      setNoteFor({});
      setManagerFor({});
      setRateFor({});
      queryClient.invalidateQueries({ queryKey: ["admin-timesheet-submissions"] });
    },
    onError: (err) =>
      setError(extractErrorMessage(err, "Could not record this decision.")),
  });

  if (!isAdmin) {
    return (
      <div>
        <h1 style={pageTitle}>Approvals</h1>
        <p style={{ color: "var(--mute)", marginTop: 8 }}>
          Admin-only. This is the workspace admin's queue for weekly timesheet
          submissions.
        </p>
      </div>
    );
  }

  const rows = queueQuery.data ?? [];

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h1 style={pageTitle}>Approvals</h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Weekly timesheet submissions waiting on your decision.
        </p>
      </header>

      <div style={toolbar}>
        <span style={{ fontSize: 11, color: "var(--mute)", letterSpacing: 0.6, textTransform: "uppercase" }}>
          Status
        </span>
        {(
          [
            { key: "submitted", label: "Requested timesheet" },
            { key: "download_requested", label: "Requested download" },
            { key: "approved", label: "Approved" },
            { key: "rejected", label: "Rejected" },
            { key: "", label: "All" },
          ] as const
        ).map(({ key, label }) => (
          <button
            key={key || "all"}
            type="button"
            onClick={() => setStatusFilter(key)}
            aria-pressed={statusFilter === key}
            style={pillBtn(statusFilter === key)}
          >
            {label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => queueQuery.refetch()}
          style={ghostBtn}
          aria-label="Refresh"
        >
          ↻ Refresh
        </button>
      </div>

      {error && (
        <div role="alert" style={errorBanner}>
          {error}
        </div>
      )}

      {queueQuery.isLoading ? (
        <p style={{ color: "var(--mute)" }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: "var(--mute)" }}>
          {statusFilter === "submitted"
            ? "Nothing pending — the queue is clear."
            : statusFilter === "download_requested"
              ? "No download requests right now."
              : "No matching submissions."}
        </p>
      ) : (
        <div style={list}>
          {rows.map((r) => {
            const note = noteFor[r.id] ?? "";
            const isPending = decide.isPending && decide.variables?.id === r.id;
            return (
              <SubmissionCard
                key={r.id}
                submission={r}
                note={note}
                manager={managerFor[r.id] ?? ""}
                rate={rateFor[r.id] ?? ""}
                onNoteChange={(text) =>
                  setNoteFor((n) => ({ ...n, [r.id]: text }))
                }
                onManagerChange={(text) =>
                  setManagerFor((n) => ({ ...n, [r.id]: text }))
                }
                onRateChange={(text) =>
                  setRateFor((n) => ({ ...n, [r.id]: text }))
                }
                onDecide={(decision) =>
                  decide.mutate({
                    id: r.id,
                    decision,
                    note,
                    manager: managerFor[r.id] ?? "",
                    rate: rateFor[r.id] ?? "",
                  })
                }
                deciding={decide.isPending}
                decidingThis={isPending}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- card with expandable breakdown ----------

function SubmissionCard({
  submission: r,
  note,
  manager,
  rate,
  onNoteChange,
  onManagerChange,
  onRateChange,
  onDecide,
  deciding,
  decidingThis,
}: {
  submission: TimesheetSubmission;
  note: string;
  manager: string;
  rate: string;
  onNoteChange: (s: string) => void;
  onManagerChange: (s: string) => void;
  onRateChange: (s: string) => void;
  onDecide: (decision: "approve" | "reject") => void;
  deciding: boolean;
  decidingThis: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const queryClient = useQueryClient();

  const gridQuery = useQuery({
    queryKey: ["admin-submission-grid", r.id],
    queryFn: async () =>
      (
        await api.get<TimesheetGrid>(
          `/admin/timesheet-submissions/${r.id}/grid`,
        )
      ).data,
    enabled: expanded,
  });

  // Admins edit cells on a submitted timesheet via the same per-cell upsert
  // shape the employee uses, but routed through the admin endpoint that
  // stamps admin_modified_{at,by_id} and recomputes the submission total.
  const editCell = useMutation({
    mutationFn: async (patch: CellPatch) =>
      (
        await api.put(
          `/admin/timesheet-submissions/${r.id}/cell`,
          patch,
        )
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-submission-grid", r.id] });
      queryClient.invalidateQueries({ queryKey: ["admin-timesheet-submissions"] });
    },
  });

  // Second approval stage: release (or revoke) the employee's own download.
  const downloadApproval = useMutation({
    mutationFn: async (approve: boolean) =>
      (
        await api.post<TimesheetSubmission>(
          `/admin/timesheet-submissions/${r.id}/download-approval?approve=${approve}`,
        )
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-timesheet-submissions"] });
    },
  });

  const weekStart = new Date(r.week_start + "T00:00:00");
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const editable = r.status === "submitted";

  return (
    <article style={card}>
      <header style={cardHead}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          <Avatar initials={initialsOf(r.user_display_name)} title={r.user_display_name ?? ""} />
          <span>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {r.user_display_name ?? "Unknown"}
            </div>
            <div style={{ fontSize: 12, color: "var(--mute)" }}>
              {r.user_email}
              {r.user_job_id ? (
                <>
                  {" · "}
                  <span style={{ fontFamily: "var(--font-mono)" }}>
                    {r.user_job_id}
                  </span>
                </>
              ) : null}
            </div>
          </span>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {r.admin_modified_at && (
            <span
              style={modifiedBadge}
              title={
                r.admin_modified_by_display_name
                  ? `Modified by ${r.admin_modified_by_display_name}`
                  : "Modified by admin"
              }
            >
              modified
            </span>
          )}
          <span style={statusPill(r.status)}>{r.status}</span>
        </div>
      </header>

      <div style={cardBody}>
        <div>
          <div style={fieldLabel}>Week</div>
          <div style={{ fontSize: 13, color: "var(--ink)" }}>
            {fmtWeekRange(r.week_start)}
          </div>
        </div>
        <div>
          <div style={fieldLabel}>Active (billable)</div>
          <div style={{ fontSize: 13, fontFamily: "var(--font-mono)", color: "var(--ink)" }}>
            {hms(r.active_seconds ?? r.total_seconds)}
          </div>
        </div>
        <div>
          <div style={fieldLabel}>Total hours</div>
          <div style={{ fontSize: 13, fontFamily: "var(--font-mono)", color: "var(--mute)" }}>
            {hms(r.total_seconds)}
          </div>
        </div>
        <div>
          <div style={fieldLabel}>Submitted</div>
          <div style={{ fontSize: 13, color: "var(--mute)" }}>
            {new Date(r.submitted_at).toLocaleString()}
          </div>
        </div>
        {r.status !== "submitted" && (
          <div>
            <div style={fieldLabel}>Decided</div>
            <div style={{ fontSize: 13, color: "var(--mute)" }}>
              {r.decided_at ? new Date(r.decided_at).toLocaleString() : "—"}
              {r.decided_by_display_name ? ` by ${r.decided_by_display_name}` : ""}
            </div>
          </div>
        )}
      </div>

      {r.decision_note && (
        <p
          style={{
            fontSize: 13,
            color: "var(--body)",
            marginTop: 6,
            padding: "8px 10px",
            background: "var(--surface-1)",
            borderRadius: 4,
          }}
        >
          {r.decision_note}
        </p>
      )}

      {/* Manager + rate + earnings, shown once captured at approval time. */}
      {r.status === "approved" && (
        <div style={approvalMetaBox}>
          {r.manager_name && (
            <div>
              <div style={fieldLabel}>Approved by manager</div>
              <div style={{ fontSize: 13 }}>{r.manager_name}</div>
            </div>
          )}
          {r.hourly_rate_usd && (
            <div>
              <div style={fieldLabel}>Rate</div>
              <div style={{ fontSize: 13, fontFamily: "var(--font-mono)" }}>
                ${Number(r.hourly_rate_usd).toFixed(2)}/hr
              </div>
            </div>
          )}
          {r.earnings_usd && (
            <div>
              <div style={fieldLabel}>Earnings</div>
              <div
                style={{
                  fontSize: 14,
                  fontFamily: "var(--font-mono)",
                  fontWeight: 600,
                  color: "var(--accent-green)",
                }}
              >
                ${Number(r.earnings_usd).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          )}
          <div>
            <div style={fieldLabel}>Employee download</div>
            {r.download_approved ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "var(--accent-green)", fontWeight: 600 }}>
                  Released
                </span>
                <button
                  type="button"
                  onClick={() => downloadApproval.mutate(false)}
                  disabled={downloadApproval.isPending}
                  style={linkBtn}
                >
                  Revoke
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => downloadApproval.mutate(true)}
                disabled={downloadApproval.isPending}
                style={r.download_requested_at ? approveDownloadBtnPending : approveDownloadBtn}
                title={
                  r.download_requested_at
                    ? "The employee has requested this download"
                    : "Let the employee download their own timesheet"
                }
              >
                {downloadApproval.isPending
                  ? "Releasing…"
                  : r.download_requested_at
                    ? "Approve download (requested)"
                    : "Approve download"}
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "end" }}>
            <button
              type="button"
              onClick={() =>
                downloadBlob(
                  `/admin/timesheet-submissions/${r.id}/export.xlsx`,
                  `timesheet-${r.week_start}.xlsx`,
                )
              }
              style={downloadBtn}
              title="Download Excel"
            >
              ⇣ Excel
            </button>
            <button
              type="button"
              onClick={() =>
                downloadBlob(
                  `/admin/timesheet-submissions/${r.id}/export.pdf`,
                  `timesheet-${r.week_start}.pdf`,
                )
              }
              style={downloadBtn}
              title="Download PDF"
            >
              ⇣ PDF
            </button>
          </div>
        </div>
      )}

      {/* Breakdown toggle — the whole reason this card exists. Admins need
          per-day visibility before deciding, not just the aggregate total. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={breakdownToggle}
        aria-expanded={expanded}
      >
        {expanded ? "▾ Hide breakdown" : "▸ Show breakdown"}
      </button>

      {expanded && (
        <div style={{ marginTop: 10 }}>
          {editable && (
            <p style={editHint}>
              You can edit any cell below — your changes are saved on blur and the
              employee gets a "modified by admin" banner on their timesheet. After
              your edits, approve or reject as usual.
            </p>
          )}
          {gridQuery.isLoading ? (
            <p style={{ color: "var(--mute)", fontSize: 13 }}>Loading…</p>
          ) : gridQuery.data ? (
            <>
              <TimesheetGridView
                from={weekStart}
                to={weekEnd}
                rows={gridQuery.data.rows}
                totalsByDay={gridQuery.data.totals_by_day}
                loading={false}
                readOnly={!editable}
                onCellChange={(category, workDate, hours) =>
                  editCell.mutate({ category, work_date: workDate, hours })
                }
              />
              <OvertimeAnnotations totalsByDay={gridQuery.data.totals_by_day} />
            </>
          ) : (
            <p style={{ color: "#ee1d36", fontSize: 13 }}>
              Could not load the breakdown.
            </p>
          )}
        </div>
      )}

      {r.status === "submitted" && (
        <>
          {/* Manager + rate live on their own row above the decision row.
              Both are optional — backend stores only on approve and the UI
              never blocks reject. */}
          <div style={approvalInputsRow}>
            <label style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={fieldLabel}>Manager (approver)</span>
              <input
                type="text"
                placeholder="Jordan Martinez"
                value={manager}
                onChange={(e) => onManagerChange(e.target.value)}
                style={textInput}
                autoComplete="off"
              />
            </label>
            <label style={{ width: 160, display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={fieldLabel}>Rate ($/hr)</span>
              <input
                type="number"
                step="0.01"
                min="0"
                max="99999.99"
                placeholder="75.00"
                value={rate}
                onChange={(e) => onRateChange(e.target.value)}
                style={{ ...textInput, fontFamily: "var(--font-mono)" }}
                autoComplete="off"
              />
            </label>
            {rate && Number(rate) > 0 && (
              <div style={{ alignSelf: "end", paddingBottom: 8 }}>
                <span style={fieldLabel}>Projected (active × rate)</span>
                <div
                  style={{
                    fontSize: 13,
                    fontFamily: "var(--font-mono)",
                    color: "var(--accent-green)",
                  }}
                >
                  $
                  {(((r.active_seconds ?? r.total_seconds) / 3600) * Number(rate)).toLocaleString(
                    undefined,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                  )}
                </div>
              </div>
            )}
          </div>

          <div style={actionRow}>
            <input
              type="text"
              placeholder="Optional note for the employee"
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              style={noteInput}
            />
            <button
              type="button"
              onClick={() => onDecide("reject")}
              disabled={deciding}
              style={rejectBtn}
            >
              Reject
            </button>
            <button
              type="button"
              onClick={() => onDecide("approve")}
              disabled={deciding}
              style={approveBtn}
            >
              {decidingThis ? "Saving…" : "Approve"}
            </button>
          </div>
        </>
      )}
    </article>
  );
}

// Surfaces per-day overtime under the breakdown grid. Anything >8h on a
// single day is flagged in orange — matches the admin dashboard's hybrid
// OT rule so the two views agree.
function OvertimeAnnotations({
  totalsByDay,
}: {
  totalsByDay: Record<string, number>;
}) {
  const overtimes = Object.entries(totalsByDay)
    .map(([day, secs]) => ({ day, secs, overtime: Math.max(0, secs - 8 * 3600) }))
    .filter((d) => d.overtime > 0);
  if (overtimes.length === 0) return null;
  return (
    <div
      style={{
        marginTop: 10,
        padding: "8px 12px",
        borderRadius: 4,
        background: "rgba(245,140,30,0.08)",
        border: "1px solid rgba(245,140,30,0.3)",
        fontSize: 12,
        color: "#8a4b00",
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      <span style={{ fontWeight: 600 }}>Overtime ({">"} 8h/day):</span>
      {overtimes.map((d) => {
        const date = new Date(d.day + "T00:00:00");
        const label = date.toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
        });
        return (
          <span key={d.day} style={{ fontFamily: "var(--font-mono)" }}>
            {label}: {(d.secs / 3600).toFixed(2)}h
            <span style={{ color: "var(--accent-orange)", marginLeft: 4 }}>
              (+{(d.overtime / 3600).toFixed(2)}h)
            </span>
          </span>
        );
      })}
    </div>
  );
}

// ---------- styles ----------

const pageTitle: CSSProperties = {
  fontSize: 28,
  fontWeight: 600,
  letterSpacing: "-0.3px",
  margin: 0,
};

const toolbar: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  marginBottom: 16,
};

function pillBtn(active: boolean): CSSProperties {
  return {
    background: active ? "var(--ink)" : "var(--canvas)",
    color: active ? "var(--on-primary)" : "var(--body)",
    border: "1px solid var(--hairline)",
    borderRadius: 999,
    padding: "3px 12px",
    fontSize: 12,
    cursor: "pointer",
  };
}

const ghostBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "5px 10px",
  fontSize: 12,
  color: "var(--ink)",
  cursor: "pointer",
};

const list: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const card: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 16,
};

const cardHead: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 12,
};

const cardBody: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: 12,
};

const fieldLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 2,
};

const downloadBtn: CSSProperties = {
  background: "var(--canvas)",
  color: "var(--ink)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "6px 12px",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const approveDownloadBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "7px 12px",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const approveDownloadBtnPending: CSSProperties = {
  ...approveDownloadBtn,
  // A pending employee request gets a subtle amber ring to draw the eye.
  boxShadow: "0 0 0 2px rgba(255,159,10,0.45)",
};

const linkBtn: CSSProperties = {
  background: "transparent",
  color: "var(--mute)",
  border: "none",
  padding: 0,
  fontSize: 12,
  textDecoration: "underline",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const modifiedBadge: CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: 3,
  background: "rgba(122,61,255,0.10)",
  color: "#5b2bcc",
  border: "1px solid rgba(122,61,255,0.3)",
  fontFamily: "var(--font-mono)",
};

const approvalMetaBox: CSSProperties = {
  marginTop: 10,
  padding: "12px 14px",
  background: "rgba(0,215,34,0.05)",
  border: "1px solid rgba(0,215,34,0.25)",
  borderRadius: 4,
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: 12,
};

const approvalInputsRow: CSSProperties = {
  display: "flex",
  gap: 12,
  marginTop: 12,
  alignItems: "flex-end",
};

const textInput: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "6px 10px",
  fontSize: 13,
  color: "var(--ink)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const editHint: CSSProperties = {
  margin: "0 0 10px",
  padding: "8px 12px",
  fontSize: 12,
  color: "var(--body)",
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  lineHeight: 1.5,
};

const breakdownToggle: CSSProperties = {
  marginTop: 12,
  background: "transparent",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "5px 12px",
  fontSize: 12,
  color: "var(--body)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const actionRow: CSSProperties = {
  display: "flex",
  gap: 8,
  marginTop: 12,
  alignItems: "center",
};

const noteInput: CSSProperties = {
  flex: 1,
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "6px 10px",
  fontSize: 13,
  outline: "none",
};

const approveBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "7px 14px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const rejectBtn: CSSProperties = {
  background: "transparent",
  color: "#c11930",
  border: "1px solid rgba(193,25,48,0.35)",
  borderRadius: 4,
  padding: "6px 13px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const errorBanner: CSSProperties = {
  marginBottom: 12,
  padding: "8px 12px",
  fontSize: 13,
  color: "#ee1d36",
  background: "rgba(238,29,54,0.06)",
  border: "1px solid rgba(238,29,54,0.3)",
  borderRadius: 4,
};

function statusPill(status: string): CSSProperties {
  const palette =
    status === "approved"
      ? { fg: "#047a18", bg: "rgba(0,215,34,0.10)" }
      : status === "rejected"
        ? { fg: "#ee1d36", bg: "rgba(238,29,54,0.10)" }
        : { fg: "#8a5b00", bg: "rgba(255,174,19,0.12)" };
  return {
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    padding: "2px 10px",
    borderRadius: 3,
    color: palette.fg,
    background: palette.bg,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  };
}
